import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../lib/api.js'
import { ago, dateTime } from '../../../lib/format.js'
import {
  Card,
  Drawer,
  DrawerSection,
  Empty,
  Field,
  Menu,
  MenuItem,
  Notice,
  PageHead,
  Pager,
  RowsSkeleton,
  SearchInput,
  SettingRow,
  Stat,
  StatsSkeleton,
  Switch,
} from '../../../components/ui.js'
import { ConfirmDialog, toast } from '../../../components/feedback.js'
import {
  AlertIcon,
  BellIcon,
  CheckCircleIcon,
  ClockIcon,
  GearIcon,
  MoreIcon,
  PlusIcon,
  UsersIcon,
  WalletIcon,
  XCircleIcon,
} from '../../../components/icons.js'
import {
  PriorityBadge,
  ROUTING_LABEL,
  StatusBadge,
  errorMessage,
  scheduleText,
  triggerText,
  type Overview,
  type Rule,
} from './shared.js'

const PER_PAGE = 20

interface RuleList {
  items: Rule[]
  total: number
  page: number
  perPage: number
}

export function useOverview() {
  return useQuery({
    queryKey: ['admin', 'wallet-alerts', 'overview'],
    queryFn: () => api<Overview>('/v1/admin/wallet-alerts/overview'),
    refetchInterval: 60_000,
  })
}

function audienceLabel(rule: Rule): string {
  if (rule.audience.mode === 'all') return rule.audience.includeInactive ? 'All merchants' : 'All active merchants'
  if (rule.audience.mode === 'specific') return `${rule.audience.merchantIds.length} selected`
  const count = rule.audience.groups.reduce((sum, group) => sum + group.conditions.length, 0)
  return `Dynamic · ${count} condition${count === 1 ? '' : 's'}`
}

type SortKey = 'name' | 'threshold' | 'created' | 'lastTriggered' | 'priority'

export function AdminWalletAlertsPage(): React.ReactNode {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const overview = useOverview()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [channel, setChannel] = useState('')
  const [minThreshold, setMinThreshold] = useState('')
  const [maxThreshold, setMaxThreshold] = useState('')
  const [merchantId, setMerchantId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [sort, setSort] = useState<SortKey>('created')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const [confirm, setConfirm] = useState<null | { action: 'delete'; ids: string[] }>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const filters = { q: search, status, channel, minThreshold, maxThreshold, merchantId, from, to: to ? `${to}T23:59:59` : '' }
  const rules = useQuery({
    queryKey: ['admin', 'wallet-alerts', 'rules', filters, sort, dir, page],
    queryFn: () => api<RuleList>('/v1/admin/wallet-alerts/rules', { query: { ...filters, sort, dir, page, perPage: PER_PAGE } }),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })
  }

  const master = useMutation({
    mutationFn: (enabled: boolean) =>
      api('/v1/admin/wallet-alerts/settings', {
        method: 'PUT',
        body: { enabled, timezone: overview.data?.timezone ?? 'Asia/Kolkata', supportUrl: overview.data?.supportUrl ?? '' },
      }),
    onSuccess: (_, enabled) => {
      toast('ok', enabled ? 'Wallet alerts switched on' : 'Wallet alerts switched off', enabled ? undefined : 'No rule will send until you switch them back on.')
      refresh()
    },
    onError: (error) => toast('error', 'Could not change the switch', errorMessage(error)),
  })

  const bulk = useMutation({
    mutationFn: (input: { ids: string[]; action: 'activate' | 'pause' | 'delete' }) =>
      api<{ done: string[]; failed: Array<{ name: string; error: string }> }>('/v1/admin/wallet-alerts/rules/bulk', { method: 'POST', body: input }),
    onSuccess: (result, input) => {
      const verb = input.action === 'delete' ? 'Deleted' : input.action === 'pause' ? 'Paused' : 'Activated'
      if (result.done.length > 0) toast('ok', `${verb} ${result.done.length} rule${result.done.length === 1 ? '' : 's'}`)
      for (const failure of result.failed.slice(0, 3)) toast('error', `Could not activate “${failure.name}”`, failure.error)
      setSelected([])
      setConfirm(null)
      refresh()
    },
    onError: (error) => toast('error', 'Bulk action failed', errorMessage(error)),
  })

  const single = useMutation({
    mutationFn: (input: { id: string; action: 'duplicate' | 'activate' | 'pause' }) =>
      input.action === 'duplicate'
        ? api<Rule>(`/v1/admin/wallet-alerts/rules/${input.id}/duplicate`, { method: 'POST' })
        : api<Rule>(`/v1/admin/wallet-alerts/rules/${input.id}/status`, {
            method: 'POST',
            body: { status: input.action === 'activate' ? 'active' : 'paused' },
          }),
    onSuccess: (rule, input) => {
      toast('ok', input.action === 'duplicate' ? `Duplicated as “${rule.name}”` : input.action === 'activate' ? 'Rule activated' : 'Rule paused')
      refresh()
    },
    onError: (error, input) => toast('error', input.action === 'activate' ? 'Cannot activate yet' : 'Action failed', errorMessage(error)),
  })

  function onSort(key: SortKey): void {
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc')
    else {
      setSort(key)
      setDir(key === 'name' ? 'asc' : 'desc')
    }
    setPage(1)
  }

  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value)
      setPage(1)
      setSelected([])
    }
  }

  const items = rules.data?.items ?? []
  const allSelected = items.length > 0 && items.every((rule) => selected.includes(rule.id))
  const filtered = Boolean(search || status || channel || minThreshold || maxThreshold || merchantId || from || to)
  const stats = overview.data?.stats

  const header = (key: SortKey, label: string, className?: string): React.ReactNode => (
    <th className={className} aria-sort={sort === key ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}>
      <button type="button" className={sort === key ? 'th-sort is-active' : 'th-sort'} onClick={() => onSort(key)}>
        {label}
        <span aria-hidden="true">{sort === key ? (dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </button>
    </th>
  )

  return (
    <>
      <PageHead
        title="Wallet Alerts"
        subtitle="Configure automated wallet balance notifications for your merchants."
        crumbs={[{ label: 'Wallet', to: '/admin/ledger' }, { label: 'Wallet Alerts' }]}
        actions={
          <div className="wa-head-actions">
            <span className="wa-master" title="Master switch. Off stops every rule from sending.">
              <Switch
                checked={overview.data?.enabled ?? false}
                disabled={!overview.data || master.isPending}
                onChange={(next) => master.mutate(next)}
                label="Wallet alerts enabled"
              />
              {overview.data?.enabled ? 'Alerts on' : 'Alerts off'}
            </span>
            <button type="button" className="btn" onClick={() => setSettingsOpen(true)}>
              <GearIcon size={16} /> Settings
            </button>
            <Link className="btn" to="/admin/wallet-alerts/templates">
              Templates
            </Link>
            <Link className="btn" to="/admin/wallet-alerts/history">
              <ClockIcon size={16} /> Notification History
            </Link>
            <Link className="btn btn--primary" to="/admin/wallet-alerts/new">
              <PlusIcon size={16} /> Create Alert Rule
            </Link>
          </div>
        }
      />

      {overview.data && !overview.data.enabled && (
        <Notice kind="warn" title="Wallet alerts are switched off">
          Rules keep their configuration, but nothing is evaluated or sent until the master switch is on.
        </Notice>
      )}
      {overview.data && (!overview.data.channels.email || !overview.data.channels.whatsapp) && (
        <Notice kind="info" title="Channel setup">
          {!overview.data.channels.email && 'Email is not configured (Settings → Email/SMTP). '}
          {!overview.data.channels.whatsapp && 'WhatsApp (Wakit) is not configured (Settings → OTP). '}
          Rules using an unconfigured channel cannot be activated.
        </Notice>
      )}

      {!stats ? (
        <StatsSkeleton count={6} />
      ) : (
        <section className="wa-stats">
          <button type="button" className="wa-stat" title="Every rule, whatever its status." onClick={() => reset(setStatus)('')}>
            <Stat label="Total alert rules" value={stats.totalRules} icon={<BellIcon size={20} />} />
          </button>
          <button type="button" className="wa-stat" title="Rules currently being evaluated on their schedule." onClick={() => reset(setStatus)('active')}>
            <Stat label="Active rules" value={stats.activeRules} icon={<CheckCircleIcon size={20} />} tone="var(--ok)" />
          </button>
          <button
            type="button"
            className="wa-stat"
            title={`Notifications sent since midnight (${overview.data?.timezone}). Compared with yesterday.`}
            onClick={() => navigate('/admin/wallet-alerts/history?status=sent')}
          >
            <Stat label="Alerts sent today" value={stats.sentToday} icon={<WalletIcon size={20} />} tone="var(--accent)" delta={stats.sentChange} />
          </button>
          <button type="button" className="wa-stat" title="Queued or being sent right now." onClick={() => navigate('/admin/wallet-alerts/history?status=queued')}>
            <Stat label="Pending notifications" value={stats.pending} icon={<ClockIcon size={20} />} tone="var(--info)" />
          </button>
          <button
            type="button"
            className="wa-stat"
            title="Notifications that failed today, compared with yesterday."
            onClick={() => navigate('/admin/wallet-alerts/history?status=failed')}
          >
            <Stat label="Failed today" value={stats.failedToday} icon={<XCircleIcon size={20} />} tone="var(--bad)" delta={stats.failedChange} upIsGood={false} />
          </button>
          <button
            type="button"
            className="wa-stat"
            title="Distinct merchants currently past the threshold of at least one active rule. Opens the users list filtered to low wallets."
            onClick={() => navigate('/admin/users?wallet=low')}
          >
            <Stat label="Merchants below threshold" value={stats.merchantsBelow} icon={<UsersIcon size={20} />} tone="var(--warn)" />
          </button>
        </section>
      )}

      <Card flush>
        <div className="toolbar">
          <SearchInput value={search} onChange={reset(setSearch)} placeholder="Search rule name" />
          <select className="select" aria-label="Status" value={status} onChange={(e) => reset(setStatus)(e.target.value)}>
            <option value="">Status: All</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="draft">Draft</option>
          </select>
          <select className="select" aria-label="Channel" value={channel} onChange={(e) => reset(setChannel)(e.target.value)}>
            <option value="">Channel: All</option>
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <input className="input" style={{ width: 120, minWidth: 0 }} type="number" placeholder="Min ₹" aria-label="Minimum threshold" value={minThreshold} onChange={(e) => reset(setMinThreshold)(e.target.value)} />
          <input className="input" style={{ width: 120, minWidth: 0 }} type="number" placeholder="Max ₹" aria-label="Maximum threshold" value={maxThreshold} onChange={(e) => reset(setMaxThreshold)(e.target.value)} />
          <input className="input" style={{ width: 140, minWidth: 0 }} placeholder="Merchant ID" aria-label="Reaches merchant ID" value={merchantId} onChange={(e) => reset(setMerchantId)(e.target.value.replace(/\D+/g, ''))} />
          <input className="input" type="date" aria-label="Created from" value={from} onChange={(e) => reset(setFrom)(e.target.value)} />
          <input className="input" type="date" aria-label="Created to" value={to} onChange={(e) => reset(setTo)(e.target.value)} />
          {filtered && (
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                for (const set of [setSearch, setStatus, setChannel, setMinThreshold, setMaxThreshold, setMerchantId, setFrom, setTo]) set('')
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </div>

        {selected.length > 0 && (
          <div className="wa-bulkbar">
            <strong>{selected.length} selected</strong>
            <button type="button" className="btn btn--sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: selected, action: 'activate' })}>
              Enable
            </button>
            <button type="button" className="btn btn--sm" disabled={bulk.isPending} onClick={() => bulk.mutate({ ids: selected, action: 'pause' })}>
              Disable
            </button>
            <button type="button" className="btn btn--sm btn--danger" disabled={bulk.isPending} onClick={() => setConfirm({ action: 'delete', ids: selected })}>
              Delete
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setSelected([])}>
              Clear selection
            </button>
          </div>
        )}

        {rules.error && (
          <Empty error title="Could not load alert rules">
            {errorMessage(rules.error)}
          </Empty>
        )}
        {rules.isPending && <RowsSkeleton rows={6} />}

        {rules.data && items.length === 0 && !rules.error && (
          <Empty
            icon={<BellIcon size={24} />}
            title={filtered ? 'No rules match these filters' : 'No alert rules yet'}
            action={
              !filtered && (
                <Link className="btn btn--primary" to="/admin/wallet-alerts/new">
                  <PlusIcon size={16} /> Create Alert Rule
                </Link>
              )
            }
          >
            {!filtered && 'Start with a low-balance warning, then add critical and depleted levels.'}
          </Empty>
        )}

        {items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={allSelected}
                        onChange={(e) => setSelected(e.target.checked ? items.map((rule) => rule.id) : [])}
                      />
                    </th>
                    {header('name', 'Rule name')}
                    <th>Audience</th>
                    <th>Channel</th>
                    {header('threshold', 'Trigger')}
                    <th>Schedule</th>
                    {header('lastTriggered', 'Last triggered')}
                    <th>Notifications</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((rule) => (
                    <tr key={rule.id} className="is-clickable" onClick={() => navigate(`/admin/wallet-alerts/${rule.id}`)}>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${rule.name}`}
                          checked={selected.includes(rule.id)}
                          onChange={(e) => setSelected(e.target.checked ? [...selected, rule.id] : selected.filter((id) => id !== rule.id))}
                        />
                      </td>
                      <td>
                        <strong>{rule.name}</strong>
                        <span className="table__sub">
                          <PriorityBadge priority={rule.priority} />
                        </span>
                      </td>
                      <td>{audienceLabel(rule)}</td>
                      <td>{ROUTING_LABEL[rule.channels.routing]}</td>
                      <td className="num">{triggerText(rule)}</td>
                      <td>
                        {scheduleText(rule.schedule)}
                        {rule.nextRunAt && rule.status === 'active' && <span className="table__sub">Next {dateTime(rule.nextRunAt)}</span>}
                      </td>
                      <td>{rule.lastTriggeredAt ? <span title={dateTime(rule.lastTriggeredAt)}>{ago(rule.lastTriggeredAt)}</span> : <span className="faint">Never</span>}</td>
                      <td className="num">
                        <span style={{ color: 'var(--ok)' }}>{rule.counts.sent} sent</span>
                        {' · '}
                        <span style={{ color: rule.counts.failed > 0 ? 'var(--bad)' : undefined }}>{rule.counts.failed} failed</span>
                      </td>
                      <td>
                        <StatusBadge status={rule.status} />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Menu trigger={<MoreIcon size={18} />} label={`Actions for ${rule.name}`}>
                          <MenuItem onClick={() => navigate(`/admin/wallet-alerts/${rule.id}/edit`)}>Edit</MenuItem>
                          <MenuItem onClick={() => single.mutate({ id: rule.id, action: 'duplicate' })}>Duplicate</MenuItem>
                          <MenuItem onClick={() => navigate(`/admin/wallet-alerts/${rule.id}/edit?step=6`)}>Test</MenuItem>
                          {rule.status === 'active' ? (
                            <MenuItem onClick={() => single.mutate({ id: rule.id, action: 'pause' })}>Pause</MenuItem>
                          ) : (
                            <MenuItem onClick={() => single.mutate({ id: rule.id, action: 'activate' })}>Activate</MenuItem>
                          )}
                          <MenuItem danger onClick={() => setConfirm({ action: 'delete', ids: [rule.id] })}>
                            Delete
                          </MenuItem>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={page}
              pageCount={Math.max(1, Math.ceil((rules.data?.total ?? 0) / PER_PAGE))}
              total={rules.data?.total ?? 0}
              perPage={PER_PAGE}
              onPage={setPage}
              noun="rules"
            />
          </>
        )}
      </Card>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm && confirm.ids.length > 1 ? `Delete ${confirm.ids.length} rules?` : 'Delete this rule?'}
        message={
          <>
            Queued notifications for {confirm && confirm.ids.length > 1 ? 'these rules' : 'this rule'} are cancelled. Sent-notification history is kept.
            This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        danger
        pending={bulk.isPending}
        onConfirm={() => confirm && bulk.mutate({ ids: confirm.ids, action: 'delete' })}
        onClose={() => setConfirm(null)}
      />

      {settingsOpen && overview.data && <SettingsDrawer overview={overview.data} onClose={() => setSettingsOpen(false)} />}
    </>
  )
}

function SettingsDrawer({ overview, onClose }: { overview: Overview; onClose: () => void }): React.ReactNode {
  const queryClient = useQueryClient()
  const [enabled, setEnabled] = useState(overview.enabled)
  const [timezone, setTimezone] = useState(overview.timezone)
  const [supportUrl, setSupportUrl] = useState(overview.supportUrl)

  const save = useMutation({
    mutationFn: () => api('/v1/admin/wallet-alerts/settings', { method: 'PUT', body: { enabled, timezone: timezone.trim(), supportUrl: supportUrl.trim() } }),
    onSuccess: () => {
      toast('ok', 'Wallet alert settings saved')
      void queryClient.invalidateQueries({ queryKey: ['admin', 'wallet-alerts'] })
      onClose()
    },
  })

  return (
    <Drawer
      open
      onClose={onClose}
      title="Wallet alert settings"
      subtitle="Platform-wide. Each rule has its own schedule and channels."
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </button>
        </>
      }
    >
      {save.error && (
        <Notice kind="error" title="Could not save">
          {errorMessage(save.error)}
        </Notice>
      )}
      <DrawerSection title="Delivery">
        <SettingRow title="Wallet alerts enabled" description="Off stops every rule from evaluating or sending. Configuration is kept.">
          <Switch checked={enabled} onChange={setEnabled} label="Wallet alerts enabled" />
        </SettingRow>
        <Field label="Default timezone" hint="New rules start in this timezone. IANA name, e.g. Asia/Kolkata.">
          <input className="input" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
        </Field>
        <Field label="Support URL" hint="Fills {{support_url}} in templates.">
          <input className="input" value={supportUrl} placeholder="https://outfalakcheckout.com/support" onChange={(e) => setSupportUrl(e.target.value)} />
        </Field>
      </DrawerSection>
      <DrawerSection title="Channels">
        <Notice kind={overview.channels.email ? 'ok' : 'warn'} title={overview.channels.email ? 'Email: SMTP configured' : 'Email: not configured'}>
          The sender, host and credentials live in <Link to="/admin/integrations">Integrations → Email</Link>.
        </Notice>
        <Notice kind={overview.channels.whatsapp ? 'ok' : 'warn'} title={overview.channels.whatsapp ? 'WhatsApp: Wakit connected' : 'WhatsApp: Wakit not configured'}>
          Only Meta-approved templates can be sent. Link them under <Link to="/admin/wallet-alerts/templates">Templates</Link>.
        </Notice>
      </DrawerSection>
      <DrawerSection title="Permissions">
        <p className="faint" style={{ fontSize: '.84rem' }}>
          <AlertIcon size={14} /> This console has one role — platform operator — and every wallet alert action requires it. Every change is written to
          the audit log (Health &amp; Audit).
        </p>
      </DrawerSection>
    </Drawer>
  )
}
